import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { grabsApi } from "@/api/grabs";
import { moviesApi, type MovieTranslation } from "@/api/movies";
import type { GrabStatus, MinimumAvailability, MovieEdition } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, Select } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status, type StatusTone } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { BlocklistSection } from "@/components/BlocklistSection";
import { RedownloadButton } from "@/components/RedownloadButton";
import { SubtitleSearchModal } from "@/components/SubtitleSearchModal";
import { TagPills } from "@/components/TagPills";

const grabStatusTone: Record<GrabStatus, StatusTone> = {
  queued: "info",
  downloading: "info",
  completed: "good",
  imported: "good",
  failed: "bad",
  removed: "neutral",
};

export function MovieDetail() {
  const { id } = useParams({ from: "/movies/$id" });
  const movieId = Number(id);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [subtitlesOpen, setSubtitlesOpen] = useState(false);

  const { data: movie, isLoading } = useQuery({
    queryKey: ["movie", movieId],
    queryFn: () => moviesApi.get(movieId),
  });

  const { data: grabs } = useQuery({
    queryKey: ["grabs", "movie", movieId],
    queryFn: () => grabsApi.list({ media_kind: "movie", media_id: movieId }),
  });

  const searchMut = useMutation({
    mutationFn: () => moviesApi.search(movieId),
    onSuccess: (record) => {
      toast(
        `Grabbed ${record.release_title} via ${record.indexer_name ?? "?"} → ${record.download_client_name ?? "?"}`,
        "success",
      );
      qc.invalidateQueries({ queryKey: ["grabs"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Grab failed", "error"),
  });

  const removeMut = useMutation({
    mutationFn: () => moviesApi.remove(movieId),
    onSuccess: () => {
      toast("Movie removed", "success");
      qc.invalidateQueries({ queryKey: ["movies"] });
      navigate({ to: "/movies" });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  const monitorMut = useMutation({
    mutationFn: (monitored: boolean) =>
      moviesApi.update(movieId, { monitored }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["movie", movieId] });
      qc.invalidateQueries({ queryKey: ["movies"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  const availabilityMut = useMutation({
    mutationFn: (value: MinimumAvailability | "default") =>
      moviesApi.update(movieId, { minimum_availability: value }),
    onSuccess: () => {
      toast("Minimum availability updated", "success");
      qc.invalidateQueries({ queryKey: ["movie", movieId] });
      qc.invalidateQueries({ queryKey: ["movies"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  const editionMut = useMutation({
    mutationFn: (value: MovieEdition | "any") =>
      moviesApi.update(movieId, { preferred_edition: value }),
    onSuccess: () => {
      toast("Edition preference updated", "success");
      qc.invalidateQueries({ queryKey: ["movie", movieId] });
      qc.invalidateQueries({ queryKey: ["movies"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  // Migration 0062: per-movie Make-Private / Make-Public toggle and the
  // private Jellyfin Watch deep-link target. Settings query is admin-
  // only; we tolerate failure (e.g. non-admin viewer) by treating the
  // jellyfin URL as unconfigured and just hiding the Watch button.
  const settingsQ = useQuery({
    queryKey: ["movies", "settings"],
    queryFn: moviesApi.settings.get,
    retry: false,
  });
  const privateJellyfinUrl =
    settingsQ.data?.private_jellyfin_url?.trim() || null;

  const privateMut = useMutation({
    mutationFn: (next: boolean) =>
      moviesApi.update(movieId, { private: next }),
    onSuccess: (_m, next) => {
      toast(
        next
          ? "Marked private. Already-filed file stays at its current path; future grabs route to the private root."
          : "Marked public. Hidden from the household library no more.",
        "success",
      );
      qc.invalidateQueries({ queryKey: ["movie", movieId] });
      qc.invalidateQueries({ queryKey: ["movies"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton.Block height="3rem" width="22rem" />
        <div className="flex flex-col gap-6 sm:grid sm:grid-cols-[14rem_1fr] sm:gap-8">
          <Skeleton.Poster />
          <Skeleton.Text lines={5} />
        </div>
      </div>
    );
  }
  if (!movie)
    return <p className="text-sm text-foreground/55">Not found.</p>;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Movie"
        title={movie.title}
        description={movie.overview ?? undefined}
        actions={
          <>
            {/* Migration 0062: Watch deep-link. Only meaningful for
                private + filed movies, and only when the operator has
                configured the private Jellyfin URL. Opens in a new
                tab so the arr-in-one session keeps its scroll position. */}
            {movie.private && movie.has_file && privateJellyfinUrl && (
              <Button
                variant="secondary"
                onClick={() =>
                  window.open(
                    `${privateJellyfinUrl}/web/`,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                title="Open the private Jellyfin instance"
              >
                ▶ Watch
              </Button>
            )}
            {movie.trailer_url && (
              <Button
                variant="secondary"
                onClick={() =>
                  window.open(
                    movie.trailer_url ?? "",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                ▶ Trailer
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => monitorMut.mutate(!movie.monitored)}
            >
              {movie.monitored ? "Unmonitor" : "Monitor"}
            </Button>
            {/* Migration 0062: private toggle. We confirm both ways
                because flipping doesn't move an already-filed file —
                the user needs to know the change is metadata + future-
                imports, not a relocation. */}
            <Button
              variant="secondary"
              loading={privateMut.isPending}
              onClick={() => {
                const next = !movie.private;
                const msg = next
                  ? `Mark "${movie.title}" as private?\n\nThe row will be hidden from the default Library tab, and future imports will route to the private library root. Already-filed files stay at their current path — to fully migrate, delete + grab fresh.`
                  : `Mark "${movie.title}" as public?\n\nThe row will return to the default Library tab. Already-filed files stay at their current path; this is a metadata change.`;
                if (confirm(msg)) privateMut.mutate(next);
              }}
            >
              {movie.private ? "Make public" : "Make private"}
            </Button>
            <Button
              onClick={() => searchMut.mutate()}
              loading={searchMut.isPending}
            >
              Search & Grab
            </Button>
            {movie.has_file && (
              <Button
                variant="secondary"
                onClick={() => setSubtitlesOpen(true)}
              >
                Search subtitles
              </Button>
            )}
            <Button
              variant="danger"
              onClick={() => {
                if (confirm(`Remove "${movie.title}" from your library?`))
                  removeMut.mutate();
              }}
            >
              Remove
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-6 sm:grid sm:grid-cols-[14rem_1fr] sm:gap-8">
        {movie.poster_url ? (
          <img
            src={movie.poster_url}
            alt=""
            className="aspect-[2/3] w-40 self-center rounded-md border border-line object-cover shadow-elev sm:w-56 sm:self-auto"
          />
        ) : (
          <div className="grid aspect-[2/3] w-40 self-center place-items-center rounded-md border border-line bg-surface text-xs text-foreground/35 sm:w-56 sm:self-auto">
            no poster
          </div>
        )}
        <div className="space-y-6 text-sm">
          <div className="flex flex-wrap gap-2">
            {movie.has_file ? (
              <Status variant="chip" tone="good">
                downloaded
              </Status>
            ) : movie.monitored ? (
              <Status variant="chip" tone="accent">
                searching
              </Status>
            ) : (
              <Status variant="chip" tone="neutral">
                unmonitored
              </Status>
            )}
            {movie.private && (
              <span title="Hidden from the household library; routes to the private library root.">
                <Status variant="chip" tone="warn">
                  private
                </Status>
              </span>
            )}
            {movie.year && (
              <Status variant="chip" tone="neutral">
                {String(movie.year)}
              </Status>
            )}
            {movie.runtime_minutes && (
              <Status variant="chip" tone="neutral">
                {movie.runtime_minutes}m
              </Status>
            )}
            {movie.certification && (
              <Status variant="chip" tone="neutral">
                {movie.certification}
              </Status>
            )}
          </div>
          {movie.genres && movie.genres.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {movie.genres.map((g) => (
                <span
                  key={g}
                  className="rounded-full border border-line bg-surface/40 px-2.5 py-0.5 font-mono text-[11px] text-foreground/70"
                >
                  {g}
                </span>
              ))}
            </div>
          )}
          <TagPills target={{ kind: "media", mediaKind: "movie", id: movieId }} showEmpty />
          <dl className="grid grid-cols-[8rem_1fr] gap-y-2 text-xs">
            <dt className="text-foreground/45">TMDB</dt>
            <dd className="font-mono text-foreground/85">
              {movie.tmdb_id ?? "—"}
            </dd>
            <dt className="text-foreground/45">IMDB</dt>
            <dd className="font-mono text-foreground/85">
              {movie.imdb_id ?? "—"}
            </dd>
            <dt className="text-foreground/45">Path</dt>
            <dd className="font-mono break-all text-foreground/85">
              {movie.path ?? "—"}
            </dd>
          </dl>

          <ExternalLinks
            tmdbId={movie.tmdb_id}
            imdbId={movie.imdb_id}
          />

          <ReleaseDatesPanel movie={movie} />

          <Field
            label="Minimum availability"
            hint="Per-movie override of the global gate. Auto-grab won't fire until the chosen date is in the past."
          >
            <Select
              value={movie.minimum_availability ?? "default"}
              onChange={(e) =>
                availabilityMut.mutate(
                  e.target.value as MinimumAvailability | "default",
                )
              }
              disabled={availabilityMut.isPending}
            >
              <option value="default">Use global default</option>
              <option value="announced">Announced</option>
              <option value="in-cinemas">In cinemas</option>
              <option value="released">Released (digital)</option>
              <option value="physical">Physical</option>
            </Select>
          </Field>

          <Field
            label="Preferred edition"
            hint="When set, the grab selector boosts matching releases (e.g. Director's Cut, Extended) and drops mismatches if any matching release survives."
          >
            <Select
              value={movie.preferred_edition ?? "any"}
              onChange={(e) =>
                editionMut.mutate(e.target.value as MovieEdition | "any")
              }
              disabled={editionMut.isPending}
            >
              <option value="any">Any (no preference)</option>
              <option value="theatrical">Theatrical</option>
              <option value="directors-cut">Director's Cut</option>
              <option value="extended">Extended</option>
              <option value="ultimate">Ultimate</option>
              <option value="final-cut">Final Cut</option>
              <option value="remastered">Remastered</option>
              <option value="redux">Redux</option>
              <option value="unrated">Unrated</option>
              <option value="imax">IMAX</option>
              <option value="uncut">Uncut</option>
            </Select>
          </Field>
        </div>
      </div>

      <TranslationsSection movieId={movieId} />

      <BlocklistSection mediaKind="movie" mediaId={movieId} />

      <section className="space-y-3">
        <h2 className="eyebrow">Grab history</h2>
        {!grabs || grabs.length === 0 ? (
          <EmptyState
            glyph="search"
            title="No grabs yet"
            description="Click Search & Grab to query your indexers and dispatch the best release."
          />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
            <table className="w-full text-xs">
              <thead className="bg-surface-2/50">
                <tr className="text-left">
                  {[
                    "Release",
                    "Indexer",
                    "Client",
                    "Status",
                    "When",
                    "",
                  ].map((h, i) => (
                    <th
                      key={h || `col-${i}`}
                      className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {grabs.map((g) => (
                  <tr
                    key={g.id}
                    className="transition hover:bg-surface-2/40"
                  >
                    <td className="max-w-md truncate px-3 py-2.5 font-mono text-[11px]">
                      {g.release_title}
                    </td>
                    <td className="px-3 py-2.5 text-foreground/70">
                      {g.indexer_name ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 text-foreground/70">
                      {g.download_client_name ?? "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <Status
                        variant="chip"
                        tone={grabStatusTone[g.status]}
                        pulse={g.status === "downloading"}
                      >
                        {g.status}
                      </Status>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-foreground/55">
                      {new Date(g.grabbed_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <RedownloadButton
                        grabId={g.id}
                        releaseTitle={g.release_title}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SubtitleSearchModal
        open={subtitlesOpen}
        onClose={() => setSubtitlesOpen(false)}
        mediaKind="movie"
        mediaId={movieId}
        mediaTitle={movie.title}
      />
    </div>
  );
}

/**
 * Compact display of theatrical / digital / physical release dates pulled
 * from TMDB (issue #46). Unknown dates render as "—".
 */
function ReleaseDatesPanel({
  movie,
}: {
  movie: {
    theatrical_release: string | null;
    digital_release: string | null;
    physical_release: string | null;
  };
}) {
  const items: { label: string; value: string | null }[] = [
    { label: "In cinemas", value: movie.theatrical_release },
    { label: "Streaming", value: movie.digital_release },
    { label: "Disc", value: movie.physical_release },
  ];
  return (
    <div className="rounded-md border border-line bg-surface/40 px-3 py-2.5">
      <div className="eyebrow mb-1.5">Release dates</div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
        {items.map((it) => (
          <div key={it.label} className="flex items-baseline gap-1.5">
            <span className="text-foreground/45">{it.label}</span>
            <span className="font-mono text-foreground/85">
              {it.value ? formatReleaseDate(it.value) : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** "2025-01-15" → "Jan 15, 2025". Falls back to the raw string if parse
 *  fails (we'd rather show *something* than blow up the panel). */
function formatReleaseDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Issue #53: expandable list of alt-language metadata rows persisted
 * by the TMDB fan-out. Renders nothing when the user hasn't configured
 * any alt languages (the API will return an empty array). The toggle
 * stays collapsed by default — most users only glance at translations
 * occasionally; expanding fetches the rows on demand.
 */
function TranslationsSection({ movieId }: { movieId: number }) {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["movie", movieId, "translations"],
    queryFn: () => moviesApi.translations(movieId),
    enabled: expanded,
  });

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="eyebrow">Translations</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Hide" : "Show"}
        </Button>
      </div>
      {expanded &&
        (isLoading ? (
          <Skeleton.Rows count={2} />
        ) : !data || data.length === 0 ? (
          <p className="text-xs text-foreground/55">
            No alt-language metadata yet. Configure additional languages
            in Settings → Movies, or wait for the next refresh tick to
            populate them.
          </p>
        ) : (
          <div className="space-y-3">
            {data.map((t) => (
              <TranslationRow key={t.language} t={t} />
            ))}
          </div>
        ))}
    </section>
  );
}

function TranslationRow({ t }: { t: MovieTranslation }) {
  return (
    <div className="rounded-md border border-line bg-surface/40 px-3 py-2.5 text-xs">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/55">
          {t.language}
        </span>
        {t.title && (
          <span className="font-medium text-foreground/85">{t.title}</span>
        )}
      </div>
      {t.tagline && (
        <p className="mt-1 italic text-foreground/65">{t.tagline}</p>
      )}
      {t.overview && (
        <p className="mt-1.5 leading-relaxed text-foreground/75">
          {t.overview}
        </p>
      )}
      {!t.title && !t.overview && !t.tagline && (
        <p className="mt-1 text-foreground/45">
          (TMDB has no translation strings for this language yet)
        </p>
      )}
    </div>
  );
}

/**
 * Clickable jump-out links to canonical references for cast info, reviews,
 * related movies, etc (issue #54). All four URLs are derivable from the
 * `tmdb_id` (TMDB / Trakt / Letterboxd) or the `imdb_id` (IMDB), so we
 * don't persist the URLs themselves — just render anchors when the IDs
 * are present.
 *
 * Hidden entirely when `tmdb_id` is null (no canonical TMDB page → no
 * sensible Trakt or Letterboxd link either, and IMDB alone is too sparse
 * to warrant the row).
 */
function ExternalLinks({
  tmdbId,
  imdbId,
}: {
  tmdbId: number | null;
  imdbId: string | null;
}) {
  if (tmdbId == null) return null;
  const links: { label: string; href: string }[] = [
    { label: "TMDB", href: `https://www.themoviedb.org/movie/${tmdbId}` },
    { label: "Trakt", href: `https://trakt.tv/search/tmdb/${tmdbId}?id_type=movie` },
    { label: "Letterboxd", href: `https://letterboxd.com/tmdb/${tmdbId}/` },
  ];
  if (imdbId) {
    // Insert IMDB after TMDB so the order matches "primary catalog →
    // adjacent catalogs" — TMDB is our source of truth, IMDB is the
    // most-cited cross-reference, and Trakt / Letterboxd are tertiary.
    links.splice(1, 0, {
      label: "IMDB",
      href: `https://www.imdb.com/title/${imdbId}/`,
    });
  }
  return (
    <div className="rounded-md border border-line bg-surface/40 px-3 py-2.5">
      <div className="eyebrow mb-1.5">External links</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs">
        {links.map((l) => (
          <a
            key={l.label}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-accent hover:text-accent/85 hover:underline"
          >
            {l.label}
            <span aria-hidden="true" className="ml-0.5 text-accent/65">↗</span>
          </a>
        ))}
      </div>
    </div>
  );
}
